import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';
import {
  Box,
  Button,
  Link,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  Text,
  useToast,
} from '@chakra-ui/react';

import { useModalManagerContext } from '../../components/ModalManager/ModalManagerContext';
import AddressForm from '../../components/Forms/AddressForm';
import useForm from '../../utils/useForm';
import { useGlobalContext } from '../state/GlobalContext';
import { getShippingOptions } from '../../subscriptions/api';
import { getCurrencyFormatter } from '../../utils';

const UpdateAddressFormModal: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { subscriptionId, customerId, authToken } = useGlobalContext();
  const {
    loadNextFlowStep,
    loadPreviousFlowStep,
    setData,
  } = useModalManagerContext();
  const [isLoading, setIsLoading] = useState(false);
  const { values, handleChange } = useForm<ModalFormAddress>({
    initialValues: {
      country: '',
      province: '',
      firstName: '',
      lastName: '',
      company: '',
      address1: '',
      address2: '',
      city: '',
      zip: '',
      phone: '',
    },
  });
  const queryClient = useQueryClient();

  const subscription = queryClient.getQueryData<Subscription>([
    'subscription',
    customerId,
    subscriptionId,
  ]);

  const handleOnSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      const shippingOptionsRes = await getShippingOptions(
        customerId,
        subscriptionId,
        authToken,
        { deliveryAddress: values },
      );
      const shippingOptions =
        shippingOptionsRes?.deliveryOptions?.deliveryOptions || [];

      if (!shippingOptions.length) {
        showNoShippingOptionsErrorNotification();
      } else {
        const {
          DeliveryMethod,
          FormattedDeliveryPrice,
        } = subscription as Subscription;
        const draftId = shippingOptionsRes?.draftId;
        const { code, price } = shippingOptions[0];
        const { amount, currencyCode } = price;
        const format = getCurrencyFormatter(currencyCode);
        const newPrice = format(parseFloat(amount));

        setData({
          code,
          draftId,
          newPrice,
          oldPrice: FormattedDeliveryPrice,
          newDeliveryAddress: values,
          oldDeliveryAddress: DeliveryMethod?.address,
        });
        loadNextFlowStep();
      }
    } catch (error) {
      toast({
        isClosable: true,
        position: 'top',
        status: 'error',
        title: (
          <Text color="white">
            {t('cancelModal.error')}{' '}
            <Link onClick={() => handleOnSubmit(event)}>
              {t('cancelModal.tryAgain')}
            </Link>
          </Text>
        ),
        variant: 'error',
      });
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const showNoShippingOptionsErrorNotification = () => {
    toast({
      isClosable: true,
      position: 'top',
      status: 'error',
      title: t('cancelModal.address.noShippingOptions'),
      variant: 'error',
    });
  };

  return (
    <>
      <ModalHeader>{t('cancelModal.address.modalFormTitle')}</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <Box>
          <StyledForm id="update-address-form" onSubmit={handleOnSubmit}>
            <AddressForm handleChange={handleChange} values={values} />
          </StyledForm>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Button mr="4" onClick={loadPreviousFlowStep} variant="outlinedBig">
          {t('cancelModal.back')}
        </Button>
        <Button
          form="update-address-form"
          isLoading={isLoading}
          type="submit"
          variant="blackBig"
        >
          {t('titles.save')}
        </Button>
      </ModalFooter>
    </>
  );
};

const StyledForm = styled.form`
  align-items: stretch;
  column-gap: 1em;
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: space-between;
  max-height: calc(100vh - 220px);
  overflow: auto;
  padding-top: 5px;

  > div {
    flex: 1 1 100%;
  }

  @media screen and (min-width: 48em) {
    .firstName,
    .lastName {
      flex: 1 1 auto;
    }

    .address1,
    .address2,
    .city,
    .phone {
      flex: 1 1 100%;
    }

    .country,
    .province,
    .zip {
      flex: 1 1 30%;
    }
  }
`;

export default UpdateAddressFormModal;
